/**
 * RTK Query slice for the coder feature (design §12). Queries ONLY — the P5
 * panel is a status board, not a control room (P5-3), so this slice defines
 * zero mutations and needs no invalidations.
 *
 * HARD GUARD (§5.2, §12): this slice — and any workspace code — must NEVER
 * define a call to `POST /secrets/:id/read` or `/read/backplane`. Secret
 * values do not transit the browser; vault surfaces are names and keys only.
 */
import { apiSlice } from '@/store/apiSlice';

import type {
  BranchView,
  CodingJobView,
  DockerImageView,
  PullRequestView,
  RepositoryView,
  VaultDetailView,
  VaultSummaryView,
} from '../types';

interface DataEnvelope<T> {
  data?: T;
}

function unwrap<T>(raw: DataEnvelope<T> | T | undefined): T | undefined {
  if (raw && typeof raw === 'object' && 'data' in (raw as DataEnvelope<T>)) {
    return (raw as DataEnvelope<T>).data;
  }
  return raw as T | undefined;
}

const coderTaggedApi = apiSlice.enhanceEndpoints({
  addTagTypes: [
    'Images',
    'Repositories',
    'Repository',
    'Branches',
    'Vaults',
    'Vault',
    'PullRequests',
    'CodingJobs',
  ],
});

export interface GetImagesArgs {
  /** Server-side language filter (`?language=`, case-insensitive). */
  language?: string;
}

export interface GetRepositoriesArgs {
  page: number;
  limit: number;
}

export const coderApi = coderTaggedApi.injectEndpoints({
  endpoints: (builder) => ({
    /** `GET /api/daytona/images` — raw array of active registry entries. */
    getImages: builder.query<DockerImageView[], GetImagesArgs | void>({
      query: (args) => ({
        url: 'daytona/images',
        params: (args as GetImagesArgs | undefined)?.language
          ? { language: (args as GetImagesArgs).language }
          : undefined,
      }),
      transformResponse: (raw: DockerImageView[] | undefined) => raw ?? [],
      providesTags: [{ type: 'Images', id: 'LIST' }],
    }),

    /** `GET /api/repositories?page=&limit=` — `{data, pagination}` envelope. */
    getRepositories: builder.query<RepositoryView[], GetRepositoriesArgs>({
      query: ({ page, limit }) => ({ url: 'repositories', params: { page, limit } }),
      transformResponse: (raw: DataEnvelope<RepositoryView[]>) =>
        unwrap<RepositoryView[]>(raw) ?? [],
      providesTags: [{ type: 'Repositories', id: 'LIST' }],
    }),

    /** `GET /api/repositories/:id` — bare document (shared repo-name cache). */
    getRepository: builder.query<RepositoryView, string>({
      query: (id) => ({ url: `repositories/${id}` }),
      transformResponse: (raw: DataEnvelope<RepositoryView> | RepositoryView) =>
        unwrap<RepositoryView>(raw) as RepositoryView,
      providesTags: (_result, _error, id) => [{ type: 'Repository', id }],
    }),

    /** `GET /api/repositories/:repoId/branches` — raw array. */
    getBranches: builder.query<BranchView[], string>({
      query: (repoId) => ({ url: `repositories/${repoId}/branches` }),
      transformResponse: (raw: BranchView[] | undefined) => raw ?? [],
      providesTags: (_result, _error, repoId) => [{ type: 'Branches', id: repoId }],
    }),

    /** `GET /api/repositories/:repoId/pull-requests` — `{data}` envelope. */
    getPullRequests: builder.query<PullRequestView[], string>({
      query: (repoId) => ({ url: `repositories/${repoId}/pull-requests` }),
      transformResponse: (raw: DataEnvelope<PullRequestView[]>) =>
        unwrap<PullRequestView[]>(raw) ?? [],
      providesTags: (_result, _error, repoId) => [{ type: 'PullRequests', id: repoId }],
    }),

    /** `GET /api/repositories/coding-jobs?projectId=` — `{data}` envelope. */
    getCodingJobs: builder.query<CodingJobView[], string>({
      query: (projectId) => ({ url: 'repositories/coding-jobs', params: { projectId } }),
      transformResponse: (raw: DataEnvelope<CodingJobView[]>) => unwrap<CodingJobView[]>(raw) ?? [],
      providesTags: (_result, _error, projectId) => [{ type: 'CodingJobs', id: projectId }],
    }),

    /** `GET /api/secrets/vaults` — summaries (names/descriptions, NO entries). */
    getVaults: builder.query<VaultSummaryView[], void>({
      query: () => ({ url: 'secrets/vaults' }),
      transformResponse: (raw: DataEnvelope<VaultSummaryView[]>) =>
        unwrap<VaultSummaryView[]>(raw) ?? [],
      providesTags: [{ type: 'Vaults', id: 'LIST' }],
    }),

    /** `GET /api/secrets/vaults/:id` — entries as `{key, updatedAt}` only.
     * This is the whole vault read surface of the app; the value-read endpoint
     * (`POST /secrets/:id/read`) stays forbidden (design §12). */
    getVaultDetail: builder.query<VaultDetailView, string>({
      query: (vaultId) => ({ url: `secrets/vaults/${vaultId}` }),
      transformResponse: (raw: DataEnvelope<VaultDetailView>) =>
        unwrap<VaultDetailView>(raw) as VaultDetailView,
      providesTags: (_result, _error, vaultId) => [{ type: 'Vault', id: vaultId }],
    }),
  }),
});

export const {
  useGetImagesQuery,
  useGetRepositoriesQuery,
  useGetRepositoryQuery,
  useGetBranchesQuery,
  useGetPullRequestsQuery,
  useGetCodingJobsQuery,
  useGetVaultsQuery,
  useGetVaultDetailQuery,
} = coderApi;
